import React from 'react';
import { Button } from './ui/button';
import { Shield, Lock, Clock } from 'lucide-react';

const HeroSection = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="hero" className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-red-50/30 -z-10" />
      <div className="absolute inset-0 bg-grid-pattern opacity-5 -z-10" />

      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in-up">
            <div className="inline-block">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-red-100 text-red-700 text-sm font-medium">
                <Shield className="h-4 w-4 mr-2" />
                Secure & Weatherproof Storage
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Secure Container Storage in{' '}
              <span className="text-red-600">St. Osyth</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              New 20ft shipping containers with easy access, heavy-duty security, and competitive rates. 
              Store your belongings with complete peace of mind at Blackwater Farm.
            </p>

            {/* Key Benefits */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Secure Site</p>
                  <p className="text-sm text-gray-600">Lock box fitted</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Lock className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Heavy Duty Lock</p>
                  <p className="text-sm text-gray-600">Included free</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Daily Access</p>
                  <p className="text-sm text-gray-600">7am - 7pm</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Book Your Container
              </Button>
              <Button
                onClick={() => scrollToSection('pricing')}
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 hover:border-red-600 text-gray-900 hover:text-red-600 px-8 py-6 text-lg font-semibold transition-all duration-300"
              >
                View Pricing
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center space-x-6 pt-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600">100%</p>
                <p className="text-sm text-gray-600">Weatherproof</p>
              </div>
              <div className="h-12 w-px bg-gray-300" />
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600">33m³</p>
                <p className="text-sm text-gray-600">Storage Space</p>
              </div>
              <div className="h-12 w-px bg-gray-300" />
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600">£29.98</p>
                <p className="text-sm text-gray-600">From per week</p>
              </div>
            </div>
          </div>

          {/* Right Content - Image */}
          <div className="relative animate-fade-in">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_red-sails-storage/artifacts/aeyucykm_20241220_113513.jpg"
                alt="Red Sail Self Storage Facility"
                className="w-full h-auto object-cover"
                loading="eager"
                decoding="async"
                style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
              />
              {/* Overlay Badge */}
              <div className="absolute top-6 right-6 bg-white rounded-xl shadow-lg p-4" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                <p className="text-sm font-semibold text-gray-900">From</p>
                <p className="text-3xl font-bold text-red-600">£29.98</p>
                <p className="text-sm text-gray-600">per week*</p>
                <p className="text-xs text-gray-500 mt-1">*52 weeks prepaid</p>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-red-200/30 rounded-full blur-3xl -z-10" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-blue-200/30 rounded-full blur-3xl -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
